/**
 * /ai/documents — the document library.
 * Table with status/type/loan filters + search, and the multi-PDF upload
 * pipeline: sha-256 client hash → uploadUrl (dedupe) → signed PUT →
 * finalizeUpload (server verifies bytes) → auto process run (classify +
 * extract + validate + verify + xcheck) via the Pub/Sub worker.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Badge,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiUpload, FiRefreshCw, FiChevronLeft, FiChevronRight, FiZap, FiMail } from 'react-icons/fi';
import { callDocai, sha256HexOfFile } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';

const PAGE_SIZE = 50;
const STATUSES = ['queued', 'processing', 'auto_accept', 'accept_with_warnings', 'review', 'error', 'verified', 'unrouted'];
const SOURCES = ['email', 'ui-upload', 'cli', 'migration', 'arive-pull'];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

export default function AIDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]); // [{name, state, detail}]
  const fileInputRef = useRef(null);
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/documents$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiDocuments', {
        action: 'list',
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setDocs(res.documents || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast({ title: 'List failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, search, page, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // ------------------------------------------------------------- uploads
  const setUploadState = (name, state, detail = '') =>
    setUploads((prev) => prev.map((u) => (u.name === name ? { ...u, state, detail } : u)));

  const handleFiles = async (fileList) => {
    const files = [...fileList].filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (!files.length) {
      toast({ title: 'PDFs only', status: 'warning', duration: 3000 });
      return;
    }
    setUploads(files.map((f) => ({ name: f.name, state: 'hashing', detail: '' })));

    for (const file of files) {
      try {
        const sha256 = await sha256HexOfFile(file);
        setUploadState(file.name, 'requesting upload');
        const up = await callDocai('docaiDocuments', { action: 'uploadUrl', sha256, fileName: file.name });
        if (up.existing) {
          setUploadState(file.name, 'duplicate', `already in library (${up.existing.status})`);
          continue;
        }
        setUploadState(file.name, 'uploading');
        // Signed upload URL: plain PUT with the token URL (no supabase client
        // needed — the URL embeds the token).
        const putRes = await fetch(up.signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/pdf', 'x-upsert': 'true' },
          body: file,
        });
        if (!putRes.ok) throw new Error(`storage PUT failed (${putRes.status})`);
        setUploadState(file.name, 'finalizing');
        const fin = await callDocai('docaiDocuments', { action: 'finalizeUpload', sha256, fileName: file.name });
        setUploadState(file.name, 'processing', `queued as run ${fin.runId?.slice(0, 8) || ''}`);
      } catch (err) {
        setUploadState(file.name, 'failed', err.message);
      }
    }
    load();
  };

  const activeUploads = uploads.filter((u) => !['duplicate', 'failed', 'processing'].includes(u.state)).length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" spacing={3}>
        <Heading size="md">Documents</Heading>
        <HStack>
          <Button size="sm" leftIcon={<FiRefreshCw />} onClick={load} isLoading={loading} variant="outline">
            Refresh
          </Button>
          <Button
            size="sm"
            leftIcon={<FiUpload />}
            colorScheme="blue"
            onClick={() => fileInputRef.current?.click()}
            isLoading={activeUploads > 0}
            loadingText={`Uploading ${activeUploads}`}
          >
            Upload PDFs
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            hidden
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </HStack>
      </HStack>

      {/* Upload progress */}
      {uploads.length > 0 && (
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={3} mb={4}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="medium">Uploads</Text>
            <Button size="xs" variant="ghost" onClick={() => setUploads([])}>Clear</Button>
          </HStack>
          <VStack align="stretch" spacing={1}>
            {uploads.map((u) => (
              <HStack key={u.name} fontSize="xs" justify="space-between">
                <Text noOfLines={1} flex={1}>{u.name}</Text>
                <Text
                  color={u.state === 'failed' ? 'red.400' : u.state === 'duplicate' ? 'yellow.500' : u.state === 'processing' ? 'green.500' : 'blue.400'}
                  flexShrink={0}
                >
                  {u.state}{u.detail ? ` — ${u.detail}` : ''}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {/* Filters */}
      <HStack mb={4} spacing={3} flexWrap="wrap">
        <Select
          size="sm"
          maxW="220px"
          placeholder="All statuses"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <Select
          size="sm"
          maxW="180px"
          placeholder="All sources"
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setPage(0);
          }}
        >
          {SOURCES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchDraft.trim());
            setPage(0);
          }}
        >
          <HStack>
            <Input
              size="sm"
              maxW="280px"
              placeholder="Search file name or loan #"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
            <Button size="sm" type="submit" variant="outline">Search</Button>
          </HStack>
        </form>
        <Text fontSize="xs" color="gray.500">{total} document{total === 1 ? '' : 's'}</Text>
      </HStack>

      {/* Table */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {loading ? (
          <Center py={16}><Spinner color="blue.400" /></Center>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>File</Th>
                <Th>Status</Th>
                <Th>Type / Lender</Th>
                <Th>Loan #</Th>
                <Th>LOS</Th>
                <Th>Skill</Th>
                <Th>Added</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {docs.map((d) => (
                <Tr
                  key={d.id}
                  _hover={{ bg: cardBorder, cursor: 'pointer' }}
                  onClick={() => history.push(`${sectionUrl}/documents/${d.id}`)}
                >
                  <Td maxW="360px">
                    <HStack spacing={2}>
                      {d.source === 'email' && (
                        <Tooltip label={d.source_meta?.subject || 'Auto-detected from email'}>
                          <Badge colorScheme="purple" display="inline-flex" alignItems="center" gap="3px" flexShrink={0}>
                            <FiMail size="10" /> email
                          </Badge>
                        </Tooltip>
                      )}
                      <Text fontSize="xs" noOfLines={1} fontFamily="mono">{d.file_name}</Text>
                    </HStack>
                  </Td>
                  <Td><StatusChip status={d.status} /></Td>
                  <Td fontSize="xs">
                    {d.doc_type || '—'}
                    {d.lender ? ` / ${d.lender}` : ''}
                  </Td>
                  <Td fontSize="xs" fontFamily="mono">{d.loan_number || '—'}</Td>
                  <Td fontSize="xs">{d.arive_loan_id ? '✓' : '—'}</Td>
                  <Td fontSize="xs">{d.skill_name || '—'}</Td>
                  <Td fontSize="xs">{fmtDate(d.created_at)}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Tooltip label="Reprocess (classify + extract + validate)">
                      <IconButton
                        aria-label="Reprocess"
                        icon={<FiZap />}
                        size="xs"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await callDocai('docaiDocuments', { action: 'reprocess', documentId: d.id });
                            toast({ title: 'Reprocess queued', status: 'success', duration: 2500 });
                          } catch (err) {
                            toast({ title: 'Reprocess failed', description: err.message, status: 'error', duration: 5000 });
                          }
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
              {!docs.length && (
                <Tr>
                  <Td colSpan={8}>
                    <Center py={10}>
                      <Text fontSize="sm" color="gray.500">No documents match.</Text>
                    </Center>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Pagination */}
      <HStack justify="center" mt={4} spacing={3}>
        <IconButton
          aria-label="Previous page"
          icon={<FiChevronLeft />}
          size="sm"
          isDisabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        />
        <Text fontSize="sm">Page {page + 1} / {pageCount}</Text>
        <IconButton
          aria-label="Next page"
          icon={<FiChevronRight />}
          size="sm"
          isDisabled={page + 1 >= pageCount}
          onClick={() => setPage((p) => p + 1)}
        />
      </HStack>
    </Box>
  );
}
